<template>
  <div class="dropdown text-center" :class="{'displayed':isOpen}" v-click-outside="lostFocus">
    <div class="title z-10" :class="{'bg-white':isOpen}">
      <div
        class="title-text rounded-20 border-shadow border-classen__secondary bg-white grid grid-cols-6">
        <p
          class="col-span-2 bg-classen__primary text-white favourite flex items-center justify-center"
          :class="{'text-gold':allSelected}"
          @click="$emit('addSearchProductsToSelection')">
          <label class="mr-2">{{ getTranslation('all') }}</label><i class="fas fa-star"></i>
        </p>
        <p class="col-span-3 overflow-hidden whitespace-nowrap overflow-ellipsis px-1 text-left"
           @click="isOpen ? closeSelection() : openSelection()"
           :class="{'text-gray-400': selectedSelection.id === -1}">
          {{ selectedSelection.name }}
        </p>
        <div @click="isOpen ? closeSelection() : openSelection()">
          <p v-show="!isOpen" class="mr-2"><i class="fas fa-angle-down"></i></p>
          <p v-show="isOpen" class="mr-2"><i class="fas fa-angle-up "></i></p>
        </div>
      </div>
    </div>
    <div class="dropdown-content">
      <div class="h-full px-2">
        <input v-model="searchInput" type="text" :placeholder="getTranslation('search')"
               class="border-shadow searchElement rounded-20 search w-full ml-auto mr-auto mt-2 mb-2 pl-2">
        </input>

        <div class="text-left body flex mb-6 flex-col">
          <div class="h-full">
            <p class="text-xs text-gray-400 h-4">
              {{ getTranslation('suggestions') }}
            </p>
            <div class="max-h-full text-xs overscroll-contain">
              <div v-for="selection in selectionSuggestions"
                   v-on:click="setCurrentSelection(selection)"
                   class="flex mb-1 overscroll-contain selectionItem indent-text hover:bg-classen__secondary cursor-pointer">
                <p class="font-bold w-9/10 selectionName">{{ selection.name }}</p>
                <p class="w-1/10 text-center self-center">
                  <i v-if="selection.type === 'private'" class="fas fa-lock text-right"></i>
                </p>
              </div>
            </div>
          </div>
          <div class="allSelections">
            <p class="text-xs text-gray-400 h-4">
              {{ getTranslation('all') }}
            </p>
            <div class="overflow-y-auto row-span-4 allSelections text-xs overscroll-contain">
              <div @click="setCurrentSelection(defaultSelection)"
                   class="flex mt-1 overscroll-contain selectionItem indent-text hover:bg-classen__secondary cursor-pointer">
                <p class="font-bold w-9/10 selectionName">{{ defaultSelection.name }}</p>
              </div>
              <div v-for="selection in filteredSelections"
                   v-on:click="setCurrentSelection(selection)"
                   class="flex mt-1 overscroll-contain selectionItem indent-text hover:bg-classen__secondary cursor-pointer">
                <p class="font-bold w-9/10 selectionName">{{ selection.name }}</p>
                <label class="text-center self-center w-1/10">
                  <i v-if="selection.type === 'private'" class="fas fa-lock text-right"></i>
                </label>
              </div>
            </div>
          </div>
        </div>

        <form v-on:submit.prevent="createSelection()"
              class="text-left text-red-600 z-10 border-t-2 py-1 border-classen__secondary text-sm">
          <div v-if="nameExists" class="text-xs mb-1">
            *{{ getTranslation('selectionExists') }}
          </div>
          <div class="flex">
            <button v-show="!nameExists" type="submit" for="create"
                    class="text-white align-middle add_icon mr-2 bg-classen__primary">
              <i class="fas fa-plus"></i>
            </button>
            <button v-show="nameExists" type="submit" for="create"
                    class="text-white align-middle add_icon mr-2 bg-red-600">
              <i class="fas fa-times"></i>
            </button>
            <input id="create" type="text" v-model="createInput" class="text-black"
                   :placeholder="getTranslation('createNewSelection')" required autocomplete="off"/>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>

import ClickOutside from "vue-click-outside";
import EventBus from "../../services/EventBus";
import Decors from "./Decors/Decors";
import Input from "../form/Input";
import getTranslation from "../../mixins/getTranslation";
import Button from "../Button";
import Loader from "../Loader";
import getPermissions from "../../mixins/getPermissions";

export default {
  name: "SelectionPicker",

  components: {
    Button,
    Input,
    Decors,
    'c-loader': Loader
  },

  props: {
    selected: {required: true},
    allSelected: {required: false, default: false}
  },

  created() {
    EventBus.listen('close-selection', this.closeSelection);
    EventBus.listen('clear-selected-selection', this.clearSelection);
  },

  destroyed() {
    EventBus.destroy('close-selection', this.closeSelection);
    EventBus.destroy('clear-selected-selection', this.clearSelection);
  },

  mixins: [getTranslation, getPermissions],

  data() {
    return {
      isOpen: false,
      searchInput: '',
      createInput: '',
      selectedSelection: this.selected,
      hasError: true,
      defaultSelection: {id: -1, name: "Set default selection"},
    }
  },

  watch: {
    selectedSelection() {
      this.$emit('update:selected', this.selectedSelection)
    }
  },

  computed: {
    nameExists() {
      return this.selections.find((selection) => {
        return selection.name === this.createInput
      }) !== undefined;
    },

    selections() {
      let selections = this.$dashboardStore.getters.getSelections;
      if (this.activeUserHasRole(this.ROLES.SALES_ADMIN)) {
        selections = selections.filter((selection) => selection.type !== 'public');
      }
      return selections;
    },

    selectionSuggestions() {
      let selectionSuggestions = this.$dashboardStore.getters.getSelectionSuggestions;
      if (this.activeUserHasRole(this.ROLES.SALES_ADMIN)) {
        selectionSuggestions = selectionSuggestions.filter((suggestion) => suggestion.type !== 'public');
      }
      return selectionSuggestions;
    },

    filteredSelections() {
      if (this.selections.length === 0 || this.selections[0] === 'Loading') {
        return []
      }

      if (this.searchInput === '') {
        return this.selections;
      }

      return this.selections.filter((selection) => {
        return selection.name.toLowerCase().includes(this.searchInput.toLowerCase());
      })
    },
  },

  methods: {
    lostFocus() {
      this.isOpen = false;
    },

    clearSelection() {
      this.selectedSelection = {id: -1, name: 'Set default selection'}
    },

    setCurrentSelection(selection) {
      this.selectedSelection = selection
      this.isOpen = false;
    },

    createSelection() {
      if (this.createInput.length === 0 || this.nameExists) {
        this.hasError = true;
        return
      }
      let selection = {name: this.createInput, memo: "", type: 'private'};

      this.$dashboardStore.dispatch('addSelection', selection)
        .then((res) => {
          this.$dashboardStore.commit('ADD_SELECTION', res.data);
          window.location.reload();
          this.createInput = "";
        })
    },

    closeSelection() {
      this.isOpen = false
    },

    openSelection() {
      EventBus.fire('close-menus');
      EventBus.fire('close-selection');
      EventBus.fire('close-nav');
      this.isOpen = true;
    },
  },

  directives: {
    ClickOutside
  }
}
</script>

<style scoped>
.title-text {
  height: 40px;
  line-height: 40px;
}

.title {
  height: 42px;
  border-radius: 20px 20px 0 0;
}

.displayed > .title {
  border: 1px solid #EAEBEC;
  border-bottom: none;
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-content {
  display: none;
  position: absolute;
  background-color: #FFFFFF;
  width: 100%;
  height: fit-content;
  z-index: 1;
  border-radius: 0 0 20px 0;
  border: 1px solid #EAEBEC;
  border-top: none;
}

.displayed .dropdown-content {
  display: block;
}

.favourite {
  border-radius: 40px 0 0 40px;
}

.search {
  height: 35px;
}

.add_icon {
  height: 20px;
  width: 20px;
  border-radius: 10px;
  text-align: center;
}

.body {
  grid-template-rows: 120px 190px;
}

.selectionItem::first-letter {
  text-transform: capitalize;
}

.selectionItem {
  min-height: 20px;
}

.allSelections {
  max-height: 190px
}

.searchElement::placeholder {
  color: #c7c7c7;
}

.selectionName {
  word-spacing: 3px;
}
</style>
